import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { BeltVisual } from '@goodfellas/belt-visual'
import { Colors } from '@goodfellas/graduation'

type BeltOption = {
  color: Colors
  label: string
  group: 'Junior' | 'Adult' | 'Professional'
}

export type BeltSelectProps = {
  id?: string
  name?: string
  value: Colors | ''
  onChange: (color: Colors) => void
  labels: Record<string, string>
  placeholder?: string
  className?: string
}

// SSR-safe: use useEffect on server (useLayoutEffect does nothing there), useLayoutEffect on client
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Render Adult first so "White" is the first option in the list
const groupsOrder: BeltOption['group'][] = ['Adult', 'Junior', 'Professional']

function getDefaultPlaceholder(labels: Record<string, string>) {
  return labels.choose || labels.placeholder || 'Select'
}

function buildOptions(labels: Record<string, string>): BeltOption[] {
  return [
    // Junior (kids) - simplified: only base colors
    { color: Colors.Gray, label: labels.gray ?? 'Gray', group: 'Junior' },
    { color: Colors.Yellow, label: labels.yellow ?? 'Yellow', group: 'Junior' },
    { color: Colors.Orange, label: labels.orange ?? 'Orange', group: 'Junior' },
    { color: Colors.Green, label: labels.green ?? 'Green', group: 'Junior' },

    // Adult
    { color: Colors.White, label: labels.white ?? 'White', group: 'Adult' },
    { color: Colors.Blue, label: labels.blue ?? 'Blue', group: 'Adult' },
    { color: Colors.Purple, label: labels.purple ?? 'Purple', group: 'Adult' },
    { color: Colors.Brown, label: labels.brown ?? 'Brown', group: 'Adult' },

    // Professional
    {
      color: Colors.Black,
      label: labels.black ?? 'Black',
      group: 'Professional',
    },
    {
      color: Colors.RedBlack,
      label: labels.redBlack ?? 'Coral Red and Black',
      group: 'Professional',
    },
    {
      color: Colors.RedWhite,
      label: labels.redWhite ?? 'Coral Red and White',
      group: 'Professional',
    },
    { color: Colors.Red, label: labels.red ?? 'Red', group: 'Professional' },
  ]
}

function groupLabel(
  group: BeltOption['group'],
  labels: Record<string, string>,
) {
  if (group === 'Junior') return labels.juniorGroup ?? 'Juvenil'
  if (group === 'Adult') return labels.adultGroup ?? 'Adulto'
  return labels.professionalGroup ?? 'Profissional'
}

export default function BeltSelect({
  id,
  name,
  value,
  onChange,
  labels,
  placeholder,
  className,
}: BeltSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [dropdownRect, setDropdownRect] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)

  const options = useMemo(() => buildOptions(labels), [labels])

  const selectedOption = useMemo(() => {
    if (!value) return null
    return options.find((o) => o.color === value) ?? null
  }, [options, value])

  const filteredBySearch = useMemo(() => {
    const s = search.trim().toLowerCase()
    if (!s) return options
    return options.filter((o) => o.label.toLowerCase().includes(s))
  }, [options, search])

  const grouped = useMemo(() => {
    const byGroup = new Map<BeltOption['group'], BeltOption[]>()
    for (const g of groupsOrder) byGroup.set(g, [])
    for (const opt of filteredBySearch) {
      byGroup.get(opt.group)?.push(opt)
    }
    return byGroup
  }, [filteredBySearch])

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      const inButton = buttonRef.current?.contains(target)
      const inDropdown = dropdownRef.current?.contains(target)
      if (!inButton && !inDropdown) setIsOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const updateRect = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    setDropdownRect({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    })
  }

  useIsomorphicLayoutEffect(() => {
    if (!isOpen) return
    updateRect()
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onResize = () => updateRect()
    const onScroll = () => updateRect()
    window.addEventListener('resize', onResize)
    // capture scroll from containers too
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [isOpen])

  const handleSelect = (color: Colors) => {
    onChange(color)
    setIsOpen(false)
    setSearch('')
  }

  const placeholderText = placeholder ?? getDefaultPlaceholder(labels)

  return (
    <div ref={containerRef} className={className}>
      <div className="relative">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((v) => !v)}
          ref={buttonRef}
          className="w-full text-gray-900 border border-gray-300 rounded-full bg-gray-50 text-base focus:ring-red-500 focus:border-red-500 pl-4 pr-10 h-14 flex items-center justify-between gap-3"
        >
          <span className="flex items-center gap-3 min-w-0">
            {selectedOption ? (
              <>
                <BeltVisual color={selectedOption.color} degree={0} size="sm" />
                <span className="truncate">{selectedOption.label}</span>
              </>
            ) : (
              <span className="text-gray-500 truncate">
                - {placeholderText} -
              </span>
            )}
          </span>
          <span className="pointer-events-none flex items-center">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </span>
        </button>

        {/* Hidden input for form compatibility */}
        <input type="hidden" id={id} name={name} value={value} readOnly />
      </div>

      {isOpen && dropdownRect
        ? createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[2147483647] bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
              role="listbox"
              style={{
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
              }}
            >
              <div className="p-3 border-b border-gray-100">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={
                    labels.searchPlaceholder ?? labels.choose ?? 'Search'
                  }
                  className="w-full px-3 py-2 rounded-xl bg-gray-100 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                />
              </div>

              <div className="max-h-80 overflow-y-auto py-2">
                {groupsOrder.map((g) => {
                  const items = grouped.get(g) ?? []
                  if (items.length === 0) return null
                  return (
                    <div key={g} className="px-2 pb-2">
                      <div className="text-xs font-semibold text-gray-400 uppercase px-2 py-1">
                        {groupLabel(g, labels)}
                      </div>
                      <div className="flex flex-col">
                        {items.map((opt) => (
                          <button
                            key={opt.color}
                            type="button"
                            role="option"
                            aria-selected={opt.color === value}
                            onClick={() => handleSelect(opt.color)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-xl flex items-center gap-3"
                          >
                            <BeltVisual
                              color={opt.color}
                              degree={0}
                              size="sm"
                            />
                            <span className="text-sm text-gray-900">
                              {opt.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
